'use client';

/**
 * How a book will look: its drawing style and its page shape, chosen by seeing them.
 *
 * The one control for this choice, wherever a book is ordered (the wizard, the New Year form).
 * Every style a parent may order gets its own tile. They come from the published illustration
 * templates (admin → Templates): title and uploaded example; the built-in catalogue and its bundled
 * tiles are the fallback.
 */
import Image from 'next/image';
import { useLocale, useTranslations } from 'next-intl';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/utils/cn';
import { staticUrl } from '@/shared/static';
import { templateTitle, useOfferedTemplates } from '@/contexts/templates/hooks/useOfferedTemplates';
import {
  OFFERED_ILLUSTRATION_STYLES,
  isComicStyle,
  type IllustrationStyleId,
} from '../services/styleTechnique';

const tileClass = (active: boolean) =>
  cn(
    'flex flex-col items-center gap-1 rounded-lg border-2 p-2 transition-all hover:border-primary/50',
    active ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border',
  );

/** A style's example picture and title; the comic sub-styles share the family's bundled picture. */
function useStyleLook() {
  const t = useTranslations('content.wizard');
  const locale = useLocale();
  const offered = useOfferedTemplates();
  const template = (key: string) => offered?.illustrations.find((i) => i.key === key);
  return {
    keys: (offered?.illustrations.length
      ? offered.illustrations.map((i) => i.key)
      : OFFERED_ILLUSTRATION_STYLES) as readonly string[],
    title: (key: string) => templateTitle(template(key)?.title, locale) ?? t(`output.bookStyles.${key}`),
    image: (key: string) => {
      const own = template(key);
      const fallback = isComicStyle(key) ? 'comic' : key;
      return own?.exampleUrl ?? staticUrl(`/img/styles/${own?.bundledPreview ?? fallback}.webp`);
    },
  };
}

export function StylePicker({
  value,
  onChange,
  label,
  testIdPrefix = 'illustration-style',
}: {
  value: string;
  onChange: (style: IllustrationStyleId) => void;
  label: string;
  testIdPrefix?: string;
}) {
  const t = useTranslations('content.wizard');
  const look = useStyleLook();
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {look.keys.map((style) => (
          <button
            key={style}
            type="button"
            onClick={() => onChange(style as IllustrationStyleId)}
            aria-pressed={value === style}
            className={tileClass(value === style)}
            data-testid={`${testIdPrefix}-${style}`}
          >
            <div className="relative aspect-square w-full overflow-hidden rounded-md bg-muted">
              <Image
                src={look.image(style)}
                alt={look.title(style)}
                fill
                sizes="(max-width: 640px) 30vw, 120px"
                className="object-cover"
              />
            </div>
            <span className="text-center text-[10px] sm:text-xs">{look.title(style)}</span>
            {/* the comics share a picture, so the words tell them apart */}
            {isComicStyle(style) && (
              <span className="text-center text-[10px] leading-tight text-muted-foreground">
                {t(`output.comicStyles.${style}.hint`)}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The page shapes a new book can take, the upright 2:3 default first, drawn to scale. */
export const BOOK_PAGE_SHAPES = {
  portrait_2x3: { width: 40, height: 60 },
  print_landscape: { width: 64, height: 48 },
  print_portrait: { width: 45, height: 60 },
  square: { width: 52, height: 52 },
} as const;
export type BookPageShape = keyof typeof BOOK_PAGE_SHAPES;

export function BookFormatPicker({
  value,
  onChange,
  label,
  testIdPrefix = 'illustration-format',
}: {
  value: string;
  onChange: (format: BookPageShape) => void;
  label: string;
  testIdPrefix?: string;
}) {
  const t = useTranslations('content.wizard.output');
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 grid grid-cols-2 gap-2 sm:grid-cols-4">
        {(Object.keys(BOOK_PAGE_SHAPES) as BookPageShape[]).map((format) => (
          <button
            key={format}
            type="button"
            onClick={() => onChange(format)}
            aria-pressed={value === format}
            className={tileClass(value === format)}
            data-testid={`${testIdPrefix}-${format}`}
          >
            <div className="flex h-16 items-center justify-center">
              <div className="rounded-sm border-2 border-muted-foreground/50 bg-muted" style={BOOK_PAGE_SHAPES[format]} />
            </div>
            <span className="text-center text-[10px] leading-tight sm:text-xs">{t(`bookFormats.${format}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
